import type { PatchV2ProviderNotesByNoteIdSectionsAndSectionIndexApiArg } from '@store/generated/notes';
import { enhancedApi } from '@store/generated/notes';

/**
 * Feature-specific tag types for notes
 */
const NOTES_TAG_TYPES = ['Note', 'NoteList', 'AdminNote', 'AdminNoteList', 'NotificationSummary'] as const;

/**
 * Extended mutation args that support skipping cache invalidation and optimistic updates
 * Used for auto-save scenarios where local editor is source of truth
 */
export type PatchSectionArgs = PatchV2ProviderNotesByNoteIdSectionsAndSectionIndexApiArg & {
  /**
   * Skip cache invalidation (prevents refetch after mutation)
   * Use for autosave where editor content is already up-to-date
   */
  _skipInvalidation?: boolean;
  /**
   * Skip optimistic cache update (prevents re-render during mutation)
   * Use for local user edits where editor is source of truth
   * Prevents cursor jump by avoiding unnecessary prop changes to EditorView
   */
  _skipOptimisticUpdate?: boolean;
};

/**
 * Notes API - extends the generated API with custom tagging and injected endpoints
 */
export const notesApi = enhancedApi
  .enhanceEndpoints({
    addTagTypes: NOTES_TAG_TYPES,
    endpoints: {
      // Provider list endpoint (v2)
      getV2ProviderNotes: {
        providesTags: result =>
          result
            ? [
                ...result.items.map(note => ({
                  type: 'Note' as const,
                  id: note.note_id,
                })),
                { type: 'NoteList' as const, id: 'LIST' },
              ]
            : [{ type: 'NoteList' as const, id: 'LIST' }],
      },
      // Admin list endpoint
      getV2AdminProvidersByProviderIdNotes: {
        providesTags: result =>
          result
            ? [
                ...result.items.map(note => ({
                  type: 'AdminNote' as const,
                  id: note.note_id,
                })),
                { type: 'AdminNoteList' as const, id: 'LIST' },
              ]
            : [{ type: 'AdminNoteList' as const, id: 'LIST' }],
      },
      // Admin detail endpoint
      getV2AdminNotesByNoteId: {
        providesTags: (_result, _error, arg) => [{ type: 'AdminNote' as const, id: arg.noteId }],
      },
      // Provider detail endpoint
      getV2ProviderNotesByNoteId: {
        providesTags: (_result, _error, arg) => [{ type: 'Note' as const, id: arg.noteId }],
      },
      // Note mutations - invalidate cache to trigger refetch
      patchV2ProviderNotesByNoteId: {
        invalidatesTags: (_result, _error, arg) => [{ type: 'Note' as const, id: arg.noteId }],
        onQueryStarted: async ({ noteId, updateNoteRequest }, { dispatch, queryFulfilled }) => {
          // Optimistic update - mutate cache immediately
          const patchResult = dispatch(
            notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
              Object.assign(draft, updateNoteRequest);
            }),
          );

          try {
            await queryFulfilled;
          } catch {
            // Rollback optimistic update on error
            patchResult.undo();
          }
        },
      },
      patchV2ProviderNotesByNoteIdSectionsAndSectionIndex: {
        // Conditional invalidation: skip if _skipInvalidation flag is true (for auto-save)
        // Otherwise invalidate for production code that relies on refetch behavior
        invalidatesTags: (_result, _error, arg: PatchSectionArgs) =>
          arg._skipInvalidation ? [] : [{ type: 'Note' as const, id: arg.noteId }],
        onQueryStarted: async (
          arg: PatchSectionArgs,
          { dispatch, queryFulfilled },
        ) => {
          const { noteId, sectionIndex, editSectionRequest, _skipOptimisticUpdate } = arg;

          // Conditionally apply optimistic update based on _skipOptimisticUpdate flag
          // Skip for local user edits (editor is source of truth, prevents cursor jump)
          // Apply for external changes (AI edits, need immediate UI feedback)
          let patchResult: { undo: () => void } | undefined;

          if (!_skipOptimisticUpdate) {
            patchResult = dispatch(
              notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
                if (draft.content?.sections?.[sectionIndex]) {
                  draft.content.sections[sectionIndex].edited_text = editSectionRequest.edited_text;
                }
              }),
            );
          }

          try {
            await queryFulfilled;
          } catch {
            // Rollback optimistic update on error (only if it was applied)
            patchResult?.undo();
          }
        },
      },
      postV2ProviderNotesByNoteIdSections: {
        invalidatesTags: (_result, _error, arg) => [{ type: 'Note' as const, id: arg.noteId }],
      },
      postV2ProviderNotesByNoteIdSectionsAndSectionIndexRevert: {
        invalidatesTags: (_result, _error, arg) => [{ type: 'Note' as const, id: arg.noteId }],
        onQueryStarted: async ({ noteId, sectionIndex }, { dispatch, queryFulfilled }) => {
          // Optimistic update - mutate cache immediately
          const patchResult = dispatch(
            notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
              if (draft.content?.sections?.[sectionIndex]) {
                draft.content.sections[sectionIndex].edited_text = '';
              }
            }),
          );

          try {
            await queryFulfilled;
          } catch {
            // Rollback optimistic update on error
            patchResult.undo();
          }
        },
      },
      // Reorder sections mutation
      postV2ProviderNotesByNoteIdCosignAction: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Note' as const, id: arg.noteId },
          { type: 'NoteList' as const, id: 'LIST' },
          'NotificationSummary',
        ],
      },
      // Request edits endpoint (for billing admins and supervisors)
      postV2ProviderNotesByNoteIdRequestEdits: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Note' as const, id: arg.noteId },
          { type: 'NoteList' as const, id: 'LIST' },
          'NotificationSummary',
        ],
      },
      postV2ProviderNotesByNoteIdSectionsReorder: {
        invalidatesTags: (_result, _error, arg) => [{ type: 'Note' as const, id: arg.noteId }],
      },
      // Delete section mutation
      deleteV2ProviderNotesByNoteIdSectionsAndSectionId: {
        invalidatesTags: (_result, _error, arg) => [{ type: 'Note' as const, id: arg.noteId }],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      /**
       * Delete note mutation (legacy endpoint)
       * Uses POST /notes/delete instead of generated REST endpoint
       */
      deleteNote: builder.mutation<{ status: number }, { noteId: string }>({
        query: ({ noteId }) => ({
          url: '/notes/delete',
          method: 'POST',
          body: { note_id: noteId },
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Note', id: arg.noteId },
          { type: 'NoteList', id: 'LIST' },
        ],
      }),

      /**
       * Download signed note PDF
       * Returns the PDF as a Blob for client-side download
       */
      getSignedNotePdf: builder.query<Blob, { noteId: string }>({
        query: ({ noteId }) => ({
          url: `/v2/provider/notes/${noteId}/signed-pdf`,
          responseHandler: async (response: Response) => response.blob(),
        }),
      }),
    }),
  });

// Export hooks with user-friendly names
export const {
  // Admin hooks
  useGetV2AdminProvidersByProviderIdNotesQuery: useGetAdminNotesQuery,
  useGetV2AdminNotesByNoteIdQuery: useGetAdminNoteQuery,

  // Provider hooks
  useGetV2ProviderNotesQuery: useGetNotesListQuery,
  useGetV2ProviderNotesByNoteIdQuery: useGetNoteQuery,
  useGetV2ProviderNotesByNoteIdActionsSuggestedQuery: useGetNoteSuggestedActionsQuery,

  // Section management hooks
  usePostV2ProviderNotesByNoteIdSectionsMutation: useAddSectionsToNoteMutation,
  usePostV2ProviderNotesByNoteIdSectionsReorderMutation: useReorderSectionsMutation,
  useDeleteV2ProviderNotesByNoteIdSectionsAndSectionIdMutation: useDeleteSectionMutation,

  // Workflow hooks
  usePostV2ProviderNotesWorkflowsEditNoteMutation: useEditNoteMutation,
  useGetV2ProviderNotesWorkflowsStatusByTaskIdQuery: useGetEditNoteStatusQuery,

  // Note mutation hooks
  usePatchV2ProviderNotesByNoteIdMutation: useUpdateNoteMutation,
  usePatchV2ProviderNotesByNoteIdSectionsAndSectionIndexMutation: useUpdateNoteSectionMutation,
  usePostV2ProviderNotesByNoteIdSectionsMutation: useAddNoteSectionsMutation,
  usePostV2ProviderNotesByNoteIdSectionsAndSectionIndexRevertMutation: useRevertNoteSectionMutation,

  // Template mutation hooks
  usePatchV2ProviderNotesByNoteIdTemplateMutation: useUpdateNoteTemplateMutation,

  // Delete note hook (injected endpoint)
  useDeleteNoteMutation,

  // Signed PDF download hook (injected endpoint)
  useLazyGetSignedNotePdfQuery,

  // Co-sign action hook
  usePostV2ProviderNotesByNoteIdCosignActionMutation: useCosignActionMutation,

  // Request edits hook (for billing admins and supervisors)
  usePostV2ProviderNotesByNoteIdRequestEditsMutation: useRequestEditsMutation,
} = notesApi;
